export const HEALTH_PLAN_TASK_TYPE = {
  REFERRAL: 'REFERRAL',
  PRESCRIPTION: 'PRESCRIPTION',
  EMERGENCY: 'EMERGENCY',
  TEST_REQUEST: 'TEST_REQUEST',
  EATING: 'EATING',
  MOOD: 'MOOD',
  SLEEP: 'SLEEP',
  PHYSICAL_ACTIVITY: 'PHYSICAL_ACTIVITY',
  OTHERS: 'OTHERS'
}

/**
 * Map for each field of task
 *
 * name(required): component name
 * url(required): component url
 * attribute(required): component attribute field (api)
 * visibilityControl(optional): define if component start visible or not
 *      visible: if is visible at moment
 *      icon: visibility icon to show on footer
 *      tooltip: the tooltip label
 * validate(required): validator method of the field,
 * position(required): field position on card
 */
export const HEALTH_PLAN_TASK_FIELDS = {
  DEADLINE: () => ({
    name: 'HPDeadline',
    url: 'components/healthPlan/creation/molecules/fields/HPDeadline',
    attribute: 'deadline',
    visibilityControl: {
      visible: false,
      icon: 'icCalendar',
      tooltip: 'Duração'
    },
    validate: () => true,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.CENTER
  }),
  FREQUENCY: () => ({
    name: 'HPFrequency',
    url: 'components/healthPlan/creation/molecules/fields/HPFrequency',
    attribute: 'frequency',
    validate: task => !!task.frequency,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.CENTER
  }),
  SPECIALTY: () => ({
    name: 'HPSpecialty',
    url: 'components/healthPlan/creation/molecules/fields/HPSpecialty',
    attribute: 'specialty',
    validate: task => !!task.specialty,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.CENTER
  }),
  SUB_SPECIALTY: () => ({
    name: 'HPSubSpecialty',
    url: 'components/healthPlan/creation/molecules/fields/HPSubSpecialty',
    attribute: 'subSpecialty',
    validate: task => !!task.subSpecialty,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.CENTER
  }),
  SPECIALIST: () => ({
    name: 'HPSpecialist',
    url: 'components/healthPlan/creation/molecules/fields/HPSpecialist',
    attribute: 'suggestedSpecialist',
    validate: task => {
      if (!task.suggestedSpecialist) return true

      const memberTier = task.suggestedSpecialist?.memberTier

      const isNotOnTier =
        memberTier &&
        !memberTier.isInMemberTier &&
        (!memberTier.suggestedReason ||
          (memberTier.suggestedReason?.reason === REFERRAL_TIER_REASON.OTHER &&
            !memberTier.suggestedReason?.description))
      return !isNotOnTier
    },
    position: HEALTH_PLAN_TASK_FIELD_POSITION.CENTER
  }),
  LOCATION: () => ({
    name: 'HPLocation',
    url: 'components/healthPlan/creation/molecules/fields/HPLocation',
    attribute: 'cityId',
    visibilityControl: {
      visible: false,
      icon: 'icLocalPin',
      tooltip: 'Localização'
    },
    validate: () => true,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.CENTER
  }),
  DIAGNOSTIC: () => ({
    name: 'HPDiagnostic',
    url: 'components/healthPlan/creation/molecules/fields/HPDiagnostic',
    attribute: 'diagnosticHypothesis',
    validate: () => true,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.BOTTOM,
    visibilityControl: {
      visible: false,
      icon: 'icMegaphone',
      tooltip: 'Orientação ao especialista'
    }
  }),
  SESSIONS_QUANTITY: () => ({
    name: 'HPSessionsQuantity',
    url: 'components/healthPlan/creation/molecules/fields/HPSessionsQuantity',
    attribute: 'sessionsQuantity',
    validate: task => {
      return (
        !task.specialty?.isTherapy ||
        (task.specialty?.isTherapy && !!task.sessionsQuantity)
      )
    },
    position: HEALTH_PLAN_TASK_FIELD_POSITION.CENTER
  }),
  PACKING: () => ({
    name: 'HPPacking',
    url: 'components/healthPlan/creation/molecules/fields/HPPacking',
    attribute: 'packing',
    validate: task => !!task.packing,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.CENTER
  }),
  MEDICINE: () => ({
    name: 'HPMedicine',
    url: 'components/healthPlan/creation/molecules/fields/HPMedicine',
    attribute: 'medicine',
    validate: task => !!task.medicine,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.CENTER
  }),
  SENTENCE: () => ({
    name: 'HPSentence',
    url: 'components/healthPlan/creation/molecules/fields/HPSentence',
    attribute: 'sentence',
    validate: () => true,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.CENTER
  }),
  ACTION: () => ({
    name: 'HPAction',
    url: 'components/healthPlan/creation/molecules/fields/HPAction',
    attribute: 'action',
    validate: task => !!task.action,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.CENTER
  }),
  DOSE: () => ({
    name: 'HPDose',
    url: 'components/healthPlan/creation/molecules/fields/HPDose',
    attribute: 'dose',
    validate: task => !!task.dose,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.CENTER
  }),
  ADMINISTRATION: () => ({
    name: 'HPAdministration',
    url: 'components/healthPlan/creation/molecules/fields/HPAdministration',
    attribute: 'routeOfAdministration',
    validate: task => !!task.routeOfAdministration,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.CENTER
  }),
  START_MEDICINE: () => ({
    name: 'HPStartMedicine',
    url: 'components/healthPlan/creation/molecules/fields/HPStartMedicine',
    attribute: 'start',
    validate: task => !!task.start,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.CENTER
  }),
  TITLE: () => ({
    name: 'HPTitle',
    url: 'components/healthPlan/creation/molecules/fields/HPTitle',
    attribute: 'title',
    validate: task => !!task.title,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.PRIMARY
  }),
  DESCRIPTION: () => ({
    name: 'HPDescription',
    url: 'components/healthPlan/creation/molecules/fields/HPDescription',
    attribute: 'description',
    validate: task => !!task.description,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.PRIMARY
  }),
  DEMAND: () => ({
    name: 'HPDemand',
    url: 'components/healthPlan/creation/molecules/fields/HPDemand',
    attribute: 'caseRecordDetails',
    validate: task => !!task.caseRecordDetails?.length,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.CENTER
  }),
  MEMBER_GUIDANCE: () => ({
    name: 'HPMemberGuidance',
    url: 'components/healthPlan/creation/molecules/fields/HPMemberGuidance',
    attribute: 'memberGuidance',
    validate: () => true,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.PRIMARY
  }),
  EXAM_DESCRIPTION: () => ({
    name: 'HPExamDescription',
    url: 'components/healthPlan/creation/molecules/fields/HPExamDescription',
    attribute: 'description',
    validate: () => true,
    position: HEALTH_PLAN_TASK_FIELD_POSITION.PRIMARY
  })
}

export const HEALTH_PLAN_TASK_FIELD_POSITION = {
  PRIMARY: 'PRIMARY',
  CENTER: 'CENTER',
  BOTTOM: 'BOTTOM'
}

export const HEALTH_PLAN_TASK_TYPE_API_MAP = {
  referral: HEALTH_PLAN_TASK_TYPE.REFERRAL,
  prescription: HEALTH_PLAN_TASK_TYPE.PRESCRIPTION,
  emergency: HEALTH_PLAN_TASK_TYPE.EMERGENCY,
  testRequest: HEALTH_PLAN_TASK_TYPE.TEST_REQUEST,
  eating: HEALTH_PLAN_TASK_TYPE.EATING,
  mood: HEALTH_PLAN_TASK_TYPE.MOOD,
  sleep: HEALTH_PLAN_TASK_TYPE.SLEEP,
  physicalActivity: HEALTH_PLAN_TASK_TYPE.PHYSICAL_ACTIVITY,
  others: HEALTH_PLAN_TASK_TYPE.OTHERS
}

const HEALTH_PLAN_TASK_FILDS_HABITS = [
  HEALTH_PLAN_TASK_FIELDS.TITLE(),
  HEALTH_PLAN_TASK_FIELDS.DESCRIPTION(),
  HEALTH_PLAN_TASK_FIELDS.DEMAND(),
  HEALTH_PLAN_TASK_FIELDS.FREQUENCY(),
  HEALTH_PLAN_TASK_FIELDS.DEADLINE()
]
export const getHealthPlanTaskComponentsMap = type => {
  const map = {
    [HEALTH_PLAN_TASK_TYPE.REFERRAL]: {
      icon: 'icStethoscope',
      name: 'Encaminhamento',
      color: 'var(--gl-color-shades-red-10)',
      iconColor: 'var(--gl-color-shades-red-60)',
      cardComponent:
        'components/healthPlan/creation/organisms/cards/HPGenericCard',
      fields: [
        HEALTH_PLAN_TASK_FIELDS.TITLE(),
        HEALTH_PLAN_TASK_FIELDS.DESCRIPTION(),
        HEALTH_PLAN_TASK_FIELDS.DEMAND(),
        HEALTH_PLAN_TASK_FIELDS.DEADLINE(),
        HEALTH_PLAN_TASK_FIELDS.SPECIALTY(),
        HEALTH_PLAN_TASK_FIELDS.SUB_SPECIALTY(),
        HEALTH_PLAN_TASK_FIELDS.SPECIALIST(),
        HEALTH_PLAN_TASK_FIELDS.LOCATION(),
        HEALTH_PLAN_TASK_FIELDS.SESSIONS_QUANTITY(),
        HEALTH_PLAN_TASK_FIELDS.DIAGNOSTIC()
      ]
    },
    [HEALTH_PLAN_TASK_TYPE.EMERGENCY]: {
      name: 'Encaminhamento para Pronto Socorro',
      icon: 'icHospital',
      color: 'var(--gl-color-shades-gray-30)',
      iconColor: 'var(--gl-color-shades-gray-80)',
      cardComponent:
        'components/healthPlan/creation/organisms/cards/HPGenericCard',
      fields: [
        HEALTH_PLAN_TASK_FIELDS.TITLE(),
        HEALTH_PLAN_TASK_FIELDS.DESCRIPTION(),
        HEALTH_PLAN_TASK_FIELDS.DEMAND(),
        HEALTH_PLAN_TASK_FIELDS.SPECIALTY(),
        HEALTH_PLAN_TASK_FIELDS.LOCATION(),
        HEALTH_PLAN_TASK_FIELDS.DIAGNOSTIC()
      ]
    },
    [HEALTH_PLAN_TASK_TYPE.PRESCRIPTION]: {
      name: 'Medicamentos',
      icon: 'icPill',
      color: 'var(--gl-color-shades-orange-10)',
      iconColor: 'var(--gl-color-shades-orange-80)',
      cardComponent:
        'components/healthPlan/creation/organisms/cards/HPPrescriptionCard',
      fields: [
        HEALTH_PLAN_TASK_FIELDS.TITLE(),
        HEALTH_PLAN_TASK_FIELDS.DESCRIPTION(),
        HEALTH_PLAN_TASK_FIELDS.DEMAND(),
        HEALTH_PLAN_TASK_FIELDS.MEDICINE(),
        HEALTH_PLAN_TASK_FIELDS.SENTENCE(),
        HEALTH_PLAN_TASK_FIELDS.PACKING(),
        HEALTH_PLAN_TASK_FIELDS.ACTION(),
        HEALTH_PLAN_TASK_FIELDS.DOSE(),
        HEALTH_PLAN_TASK_FIELDS.ADMINISTRATION(),
        HEALTH_PLAN_TASK_FIELDS.START_MEDICINE()
      ]
    },
    [HEALTH_PLAN_TASK_TYPE.TEST_REQUEST]: {
      name: 'Exames e Procedimentos',
      icon: 'icLab',
      color: 'var(--gl-color-shades-fuchsia-10)',
      iconColor: 'var(--gl-color-shades-fuchsia-70)',
      cardComponent:
        'components/healthPlan/creation/organisms/cards/HPExamCard',
      fields: [
        HEALTH_PLAN_TASK_FIELDS.DEMAND(),
        HEALTH_PLAN_TASK_FIELDS.DEADLINE(),
        HEALTH_PLAN_TASK_FIELDS.LOCATION(),
        HEALTH_PLAN_TASK_FIELDS.MEMBER_GUIDANCE()
      ]
    },
    [HEALTH_PLAN_TASK_TYPE.PHYSICAL_ACTIVITY]: {
      name: 'Atividade Física',
      icon: 'icSport',
      color: 'var(--gl-color-shades-yellow-10)',
      iconColor: 'var(--gl-color-shades-yellow-60)',
      cardComponent:
        'components/healthPlan/creation/organisms/cards/HPGenericCard',
      fields: HEALTH_PLAN_TASK_FILDS_HABITS
    },
    [HEALTH_PLAN_TASK_TYPE.EATING]: {
      name: 'Alimentação',
      icon: 'icFood',
      color: 'var(--gl-color-shades-green-10)',
      iconColor: 'var(--gl-color-shades-green-80)',
      cardComponent:
        'components/healthPlan/creation/organisms/cards/HPGenericCard',
      fields: HEALTH_PLAN_TASK_FILDS_HABITS
    },
    [HEALTH_PLAN_TASK_TYPE.SLEEP]: {
      name: 'Sono',
      icon: 'icBed',
      color: 'var(--gl-color-shades-violet-10)',
      iconColor: 'var(--gl-color-shades-violet-80)',
      cardComponent:
        'components/healthPlan/creation/organisms/cards/HPGenericCard',
      fields: HEALTH_PLAN_TASK_FILDS_HABITS
    },
    [HEALTH_PLAN_TASK_TYPE.MOOD]: {
      name: 'Humor',
      icon: 'icMindfulness',
      color: 'var(--gl-color-shades-blue-10)',
      iconColor: 'var(--gl-color-shades-blue-50)',
      cardComponent:
        'components/healthPlan/creation/organisms/cards/HPGenericCard',
      fields: HEALTH_PLAN_TASK_FILDS_HABITS
    },
    [HEALTH_PLAN_TASK_TYPE.OTHERS]: {
      name: 'Cuidados Pessoais',
      icon: 'icBandaid',
      color: 'var(--gl-color-shades-lime-10)',
      iconColor: 'var(--gl-color-shades-lime-80)',
      cardComponent:
        'components/healthPlan/creation/organisms/cards/HPGenericCard',
      fields: HEALTH_PLAN_TASK_FILDS_HABITS
    }
  }
  return type ? { ...map[type] } : map
}

export const EXAM_TYPE = {
  PACKAGE: 'PACKAGE',
  TEST: 'TEST'
}

export const EXAM_PACKAGE_STATUS = {
  REVIEWED: 'REVIEWED',
  START: 'START',
  ERROR: 'ERROR',
  ADHERENCE_ERROR: 'ADHERENCE_ERROR'
}

export const EXAM_PACKAGE_STATUS_MAP = {
  REVIEWED: 'Edição concluída',
  START: 'Aguardando revisão',
  ERROR: '1 ou mais campos não foram preenchidos',
  ADHERENCE_ERROR: 'Exame não aderente as lógicas de saúde'
}

//Presentation
export const HEALTH_PLAN_RESUME_FIELDS = {
  FREQUENCY: {
    name: 'HPFrequency',
    url: 'components/healthPlan/presentation/cardTask/atoms/resume/HPFrequency',
    hasValue: task => task.frequency
  },
  DEADLINE: {
    name: 'HPDeadline',
    url: 'components/healthPlan/presentation/cardTask/atoms/resume/HPDeadline',
    hasValue: task => task.deadline
  },
  START: {
    name: 'HPStart',
    url: 'components/healthPlan/presentation/cardTask/atoms/resume/HPStart',
    hasValue: task => task.start
  },
  SPECIALTY: {
    name: 'HPSpecialty',
    url: 'components/healthPlan/presentation/cardTask/atoms/resume/HPSpecialty',
    hasValue: task => task.specialty
  },
  SUB_SPECIALTY: {
    name: 'HPSubSpecialty',
    url: 'components/healthPlan/presentation/cardTask/atoms/resume/HPSubSpecialty',
    hasValue: task => task.subSpecialty
  },
  SPECIALIST: {
    name: 'HPSpecialist',
    url: 'components/healthPlan/presentation/cardTask/atoms/resume/HPSpecialist',
    hasValue: task => task.suggestedSpecialist
  },
  SESSIONS_QUANTITY: {
    name: 'HPSessionsQuantity',
    url: 'components/healthPlan/presentation/cardTask/atoms/resume/HPSessionsQuantity',
    hasValue: task => task.sessionsQuantity
  },
  LOCATION: {
    name: 'HPLocation',
    url: 'components/healthPlan/presentation/cardTask/atoms/resume/HPLocation',
    hasValue: task => task.location
  },
  SENTENCE: {
    name: 'HPSentence',
    url: 'components/healthPlan/presentation/cardTask/atoms/resume/HPSentence',
    hasValue: task => task.sentence
  },
  PACKING: {
    name: 'HPPacking',
    url: 'components/healthPlan/presentation/cardTask/atoms/resume/HPPacking',
    hasValue: task => task.packing
  },
  MEDICINE_TYPE: {
    name: 'HPMedicineType',
    url: 'components/healthPlan/presentation/cardTask/atoms/resume/HPMedicineType',
    hasValue: task => task.medicine?.type
  },
  MEMBER_GUIDANCE: {
    name: 'HPMemberGuidance',
    url: 'components/healthPlan/presentation/cardTask/atoms/resume/HPMemberGuidance',
    hasValue: task => task.memberGuidance
  },
  REQUESTER: {
    name: 'HPRequester',
    url: 'components/healthPlan/presentation/cardTask/atoms/resume/HPRequester',
    hasValue: task => task.lastRequester
  }
}

const HABITS_RESUME_MAP = [
  HEALTH_PLAN_RESUME_FIELDS.FREQUENCY,
  HEALTH_PLAN_RESUME_FIELDS.DEADLINE,
  HEALTH_PLAN_RESUME_FIELDS.START,
  HEALTH_PLAN_RESUME_FIELDS.REQUESTER
]

export const HEALTH_PLAN_TASK_RESUME_MAP = {
  [HEALTH_PLAN_TASK_TYPE.EATING]: HABITS_RESUME_MAP,
  [HEALTH_PLAN_TASK_TYPE.SLEEP]: HABITS_RESUME_MAP,
  [HEALTH_PLAN_TASK_TYPE.MOOD]: HABITS_RESUME_MAP,
  [HEALTH_PLAN_TASK_TYPE.OTHERS]: HABITS_RESUME_MAP,
  [HEALTH_PLAN_TASK_TYPE.PHYSICAL_ACTIVITY]: HABITS_RESUME_MAP,
  [HEALTH_PLAN_TASK_TYPE.PRESCRIPTION]: [
    HEALTH_PLAN_RESUME_FIELDS.MEDICINE_TYPE,
    HEALTH_PLAN_RESUME_FIELDS.PACKING,
    HEALTH_PLAN_RESUME_FIELDS.SENTENCE,
    HEALTH_PLAN_RESUME_FIELDS.START,
    HEALTH_PLAN_RESUME_FIELDS.DEADLINE,
    HEALTH_PLAN_RESUME_FIELDS.REQUESTER
  ],
  [HEALTH_PLAN_TASK_TYPE.REFERRAL]: [
    HEALTH_PLAN_RESUME_FIELDS.START,
    HEALTH_PLAN_RESUME_FIELDS.DEADLINE,
    HEALTH_PLAN_RESUME_FIELDS.SPECIALTY,
    HEALTH_PLAN_RESUME_FIELDS.SUB_SPECIALTY,
    HEALTH_PLAN_RESUME_FIELDS.SPECIALIST,
    HEALTH_PLAN_RESUME_FIELDS.SESSIONS_QUANTITY,
    HEALTH_PLAN_RESUME_FIELDS.LOCATION,
    HEALTH_PLAN_RESUME_FIELDS.REQUESTER
  ],
  [HEALTH_PLAN_TASK_TYPE.TEST_REQUEST]: [
    HEALTH_PLAN_RESUME_FIELDS.START,
    HEALTH_PLAN_RESUME_FIELDS.DEADLINE,
    HEALTH_PLAN_RESUME_FIELDS.LOCATION,
    HEALTH_PLAN_RESUME_FIELDS.MEMBER_GUIDANCE,
    HEALTH_PLAN_RESUME_FIELDS.REQUESTER
  ],
  [HEALTH_PLAN_TASK_TYPE.EMERGENCY]: [
    HEALTH_PLAN_RESUME_FIELDS.SPECIALTY,
    HEALTH_PLAN_RESUME_FIELDS.START,
    HEALTH_PLAN_RESUME_FIELDS.DEADLINE,
    HEALTH_PLAN_RESUME_FIELDS.LOCATION,
    HEALTH_PLAN_RESUME_FIELDS.REQUESTER
  ]
}
export const TASK_STATUS_NAMES = {
  CREATE: 'CREATE',
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  DONE: 'DONE',
  DELETED_BY_MEMBER: 'DELETED_BY_MEMBER'
}

import { REFERRAL_TIER_REASON } from './referral'

export const TASK_STATUS = {
  [TASK_STATUS_NAMES.CREATE]: 'Criada',
  [TASK_STATUS_NAMES.DRAFT]: 'Rascunhos',
  [TASK_STATUS_NAMES.ACTIVE]: 'Publicadas',
  [TASK_STATUS_NAMES.DONE]: 'Concluídas',
  [TASK_STATUS_NAMES.DELETED_BY_MEMBER]: 'Deletadas pelo membro'
}

export const ADHERENCE_RESULT = {
  NON_ADHERENT: 'NON_ADHERENT',
  ADHERENT: 'ADHERENT',
  WITHOUT_REFERENCE: 'WITHOUT_REFERENCE',
  NOT_ALLOWED: 'NOT_ALLOWED'
}

export const ADHERENCE_MODEL = {
  PROTOCOL: 'PROTOCOL',
  PROGRAM: 'PROGRAM'
}
